import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import classnames from 'classnames';
import * as uuid from 'uuid';
import { Button, ButtonProps } from '@dropbox/dig-components/buttons';
import { Banner, BannerProps } from '@dropbox/dig-components/banner';
import { createSimpleDataContext } from 'js/sign-components/common/create-simple-data-context';
import { trackHeapCustomEvent } from 'js/sign-components/common/heap';
import { useFlashMessages } from 'js/sign-components/notifications/flash-messages';

import styles from 'js/sign-components/notifications/notification-banners.module.css';

const messages = defineMessages({
  close: {
    id: '',
    description: 'Close button',
    defaultMessage: 'Close',
  },
});

export type NotificationBannerDataShape = {
  type?: BannerProps['type'], // default "attention"
  animate?: boolean, // default true
  withCloseButton?: boolean, // default true
  onRequestClose?: () => void,
  heapId?: string, // if defined, displayed event sent to heap
  autoClose?: boolean, // default true
  delay?: number, // default 7500
  message?: React.ReactNode | string,
  html?: string,
  actions?: ButtonProps[],
  withLeftIcon?: BannerProps['withLeftIcon']
}

export type NotificationID = string;

export type NotificationsShape = [NotificationID, NotificationBannerDataShape][];

export type NotificationBannerContextShape = {
  notifications: NotificationsShape,
  sendNotification: (data: NotificationBannerDataShape) => NotificationID,
  clearNotification: (id: NotificationID) => void,
  clearNotifications: () => void,
}

export type NotificationBannerProps = NotificationBannerDataShape & {
  id: NotificationID,
};

export const NotificationBannerContext = createSimpleDataContext<NotificationBannerContextShape>('notificationBanners');

export const useNotificationBanners = NotificationBannerContext.useContext;

export function NotificationBannerProvider({ children }: React.PropsWithChildren<{}>) {
  const flashMessages = useFlashMessages();
  const [notifications, setNotifications] = React.useState<NotificationsShape>(() => (
    flashMessages.map((message) => [
      uuid.v4(),
      message,
    ])
  ));

  // Send a new notification.
  const sendNotification = React.useCallback((data: NotificationBannerDataShape) => {
    const notificationId = uuid.v4();

    setNotifications((prevState) => ([
      ...prevState,
      [notificationId, data],
    ]));

    return notificationId;
  }, []);

  // Clears an existing notification by id.
  const clearNotification = React.useCallback((targetId) => {
    setNotifications((prevState) => {
      return prevState.filter(([id]) => id !== targetId);
    });
  }, []);

  // Clears all notifications.
  const clearNotifications = React.useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationBannerContext.Provider
      value={{
        notifications,
        sendNotification,
        clearNotification,
        clearNotifications,
      }}
    >
      {children}
    </NotificationBannerContext.Provider>
  );
}

function NotificationBanner({
  id,
  html,
  message,
  heapId,
  actions,
  onRequestClose,
  animate = true,
  withCloseButton = true,
  type = 'attention',
  autoClose = true,
  delay = 7500,
  withLeftIcon,
}: NotificationBannerProps) {
  const intl = useIntl();
  const { clearNotification } = useNotificationBanners();
  const [isFading, setIsFading] = React.useState(false);
  const fadeTimeout = React.useRef<NodeJS.Timeout>();
  const closeTimeout = React.useRef<NodeJS.Timeout>();

  const close = React.useCallback(() => {
    clearNotification(id);
  }, [clearNotification, id]);

  const onRequestCloseProxy = React.useCallback(() => {
    if (onRequestClose) onRequestClose();

    close();
  }, [close, onRequestClose]);

  const stopFadeTimeout = React.useCallback(() => {
    setIsFading(false);

    if (fadeTimeout.current) {
      clearTimeout(fadeTimeout.current);
    }
  }, []);

  const stopCloseTimeout = React.useCallback(() => {
    stopFadeTimeout();

    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
  }, [stopFadeTimeout]);

  const startFadeTimeout = React.useCallback(() => {
    stopFadeTimeout(); // Ensure fade timeout is stopped.

    fadeTimeout.current = setTimeout(() => {
      setIsFading(true);
    }, delay - 2500);
  }, [stopFadeTimeout, delay]);

  const startCloseTimeout = React.useCallback(() => {
    stopCloseTimeout(); // Ensure close timeout is stopped.

    startFadeTimeout();

    closeTimeout.current = setTimeout(() => {
      close();
    }, delay);
  }, [stopCloseTimeout, startFadeTimeout, close, delay]);

  React.useEffect(() => {
    if (autoClose) {
      startCloseTimeout();
    }

    if (heapId) {
      trackHeapCustomEvent('notice_displayed', { slug: heapId });
    }
  }, [autoClose, startCloseTimeout, heapId]);

  return (
    <div
      className={classnames(styles.notificationBanner, {
        [styles.isAnimated]: animate,
        [styles.isAnimated && styles.isFading]: isFading,
      })}

      // Conditional props only set if autoClose === true.
      {...autoClose && {
        onMouseEnter: stopCloseTimeout,
        onMouseLeave: startCloseTimeout,
      }}
    >
      <>
        <Banner
          type={type}
          onRequestClose={onRequestCloseProxy}
          data-testid="notification-banner"
          withLeftIcon={withLeftIcon}
          // Conditional props only set if withCloseButton === true.
          {...withCloseButton && {
            withCloseButton: intl.formatMessage(messages.close),
          }}
        >
          <Banner.Message>
            {html ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: html,
                }} />
            ) : message}
          </Banner.Message>
          {actions && actions.length > 0 && (
            <Banner.Actions>
              {actions.map((btnProps, i) => (
                <Button key={i} {...btnProps} />
              ))}
            </Banner.Actions>
          )}
        </Banner>
      </>
    </div>
  );
}

export function NotificationBanners({ className }: Pick<React.HTMLProps<HTMLDivElement>, 'className'>) {
  const { notifications } = useNotificationBanners();

  if (!notifications.length) {
    return null;
  }

  return (
    <div className={classnames(styles.notificationBanners, className)}>
      {notifications.map(([id, data]) => (
        <NotificationBanner key={id} id={id} {...data} />
      ))}
    </div>
  );
}

export function FloatingNotificationBanners() {
  return (
    <div className={styles.floatingNotificationBanners}>
      <NotificationBanners />
    </div>
  );
}
